import { useState } from 'react'
import { supabase } from '../supabaseClient'

const ORANGE = '#FF5722'
const BG = '#0D0D0D'
const CARD = '#141414'
const BORDER = '#1f1f1f'

const foodOptions = [
  '🍚 Biryani', '🍕 Pizza', '🥘 Dosa', '🍜 Ramen',
  '🍔 Burger', '🍣 Sushi', '🌮 Tacos', '🧆 Shawarma',
  '🍛 Butter Chicken', '🍝 Pasta', '🥗 Salad', '🍦 Dessert'
]

const mealTimes = [
  { label: 'Breakfast', subtitle: '7–10 AM', icon: '🌅' },
  { label: 'Brunch', subtitle: '10AM–12PM', icon: '☀️' },
  { label: 'Lunch', subtitle: '12–3 PM', icon: '🌤️' },
  { label: 'Snacks', subtitle: '3–6 PM', icon: '🌇' },
  { label: 'Dinner', subtitle: '6–11 PM', icon: '🌙' },
]

const bangaloreAreas = [
  'Koramangala', 'Indiranagar', 'HSR Layout', 'Whitefield',
  'Jayanagar', 'JP Nagar', 'Marathahalli', 'Bellandur',
  'MG Road', 'Sadashivanagar', 'Bannerghatta', 'Electronic City'
]

const sectionLabel = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1.5,
  color: '#444',
  textTransform: 'uppercase',
  marginBottom: 12
}

function chipStyle(active) {
  return {
    background: active ? `${ORANGE}22` : CARD,
    border: `1.5px solid ${active ? ORANGE : BORDER}`,
    color: active ? ORANGE : '#888',
    borderRadius: 20,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'all 0.15s'
  }
}

export default function PostCraving({ session, onNavigate }) {
  const [selectedFood, setSelectedFood] = useState('')
  const [selectedMeal, setSelectedMeal] = useState('')
  const [selectedArea, setSelectedArea] = useState('')
  const [restaurant, setRestaurant] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [locStatus, setLocStatus] = useState('idle')
  const [coords, setCoords] = useState(null)
  const [radius, setRadius] = useState(3)

  function getLocation() {
    if (!navigator.geolocation) {
      setLocStatus('denied')
      return
    }
    setLocStatus('loading')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setLocStatus('granted')
      },
      () => setLocStatus('denied')
    )
  }

  async function postCraving() {
    if (!selectedFood || !selectedMeal) return
    setLoading(true)
    setError('')

    const expires = new Date()
    expires.setHours(23, 59, 59, 0)

    const { error } = await supabase.from('cravings').insert({
      user_id: session.user.id,
      mode: 'food',
      food_type: selectedFood.split(' ').slice(1).join(' '),
      area: selectedArea || (coords
        ? `${coords.lat.toFixed(4)},${coords.lng.toFixed(4)}`
        : 'Bangalore'),
      time_slot: selectedMeal,
      restaurant_suggestion: restaurant || null,
      expires_at: expires.toISOString(),
      is_active: true
    })

    if (error) setError(error.message)
    else onNavigate('home')
    setLoading(false)
  }

  const canPost = selectedFood && selectedMeal

  return (
    <div style={{
      minHeight: '100vh',
      background: BG,
      fontFamily: "'DM Sans', sans-serif",
      paddingBottom: 48
    }}>

      {/* Header */}
      <div style={{
        padding: '52px 20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 16
      }}>
        <div
          onClick={() => onNavigate('home')}
          style={{
            width: 40, height: 40,
            borderRadius: '50%',
            background: CARD,
            border: `1.5px solid ${BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#fff',
            fontSize: 18
          }}
        >←</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>
          Post a craving
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>

        {/* FOOD */}
        <div style={sectionLabel}>What are you craving?</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 32 }}>
          {foodOptions.map(food => (
            <button
              key={food}
              onClick={() => setSelectedFood(food)}
              style={chipStyle(selectedFood === food)}
            >
              {food}
            </button>
          ))}
        </div>

        {/* MEAL TIME */}
        <div style={sectionLabel}>When?</div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 8,
          marginBottom: 32
        }}>
          {mealTimes.map(meal => (
            <div
              key={meal.label}
              onClick={() => setSelectedMeal(meal.label)}
              style={{
                background: selectedMeal === meal.label ? `${ORANGE}18` : CARD,
                border: `1.5px solid ${selectedMeal === meal.label ? ORANGE : BORDER}`,
                borderRadius: 14,
                padding: '12px 6px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              <div style={{ fontSize: 24, marginBottom: 6 }}>{meal.icon}</div>
              <div style={{
                fontSize: 12, fontWeight: 600,
                color: selectedMeal === meal.label ? ORANGE : '#ccc'
              }}>
                {meal.label}
              </div>
              <div style={{ fontSize: 9, color: '#444', marginTop: 3, lineHeight: 1.3 }}>
                {meal.subtitle}
              </div>
            </div>
          ))}
        </div>

        {/* LOCATION */}
        <div style={sectionLabel}>Your location</div>

        {locStatus === 'idle' && (
          <button
            onClick={getLocation}
            style={{
              width: '100%',
              background: CARD,
              border: `1.5px dashed ${BORDER}`,
              borderRadius: 14,
              padding: '18px',
              color: '#666',
              fontSize: 14,
              cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              marginBottom: 20
            }}
          >
            <span style={{ fontSize: 20 }}>📍</span>
            <span>Share your location</span>
          </button>
        )}

        {locStatus === 'loading' && (
          <div style={{
            background: CARD, border: `1.5px solid ${BORDER}`,
            borderRadius: 14, padding: 18,
            color: '#555', fontSize: 13,
            textAlign: 'center', marginBottom: 20
          }}>
            Finding you...
          </div>
        )}

        {locStatus === 'denied' && (
          <div style={{
            background: '#1a0a0a',
            border: '1.5px solid #ff444430',
            borderRadius: 14, padding: 18,
            color: '#ff6666', fontSize: 13,
            textAlign: 'center', marginBottom: 20
          }}>
            Location access denied. Enable it in browser settings.
          </div>
        )}

        {locStatus === 'granted' && (
          <div style={{ marginBottom: 20 }}>
            <div style={{
              background: `${ORANGE}10`,
              border: `1.5px solid ${ORANGE}30`,
              borderRadius: 14,
              padding: '14px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 20
            }}>
              <span style={{ fontSize: 20 }}>📍</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                  Location shared
                </div>
                <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>
                  {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}
                </div>
              </div>
              <div
                onClick={getLocation}
                style={{ fontSize: 11, color: ORANGE, cursor: 'pointer', fontWeight: 600 }}
              >
                Refresh
              </div>
            </div>

            {/* Radius slider */}
            <div style={{
              background: CARD,
              border: `1.5px solid ${BORDER}`,
              borderRadius: 14,
              padding: '16px 18px',
              marginBottom: 20
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 14
              }}>
                <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>
                  Match radius
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: ORANGE }}>
                  {radius} km
                </div>
              </div>
              <input
                type="range"
                min={1}
                max={15}
                value={radius}
                onChange={e => setRadius(Number(e.target.value))}
                style={{
                  width: '100%',
                  accentColor: ORANGE,
                  cursor: 'pointer'
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 10
              }}>
                {[1, 3, 5, 10, 15].map(km => (
                  <div
                    key={km}
                    onClick={() => setRadius(km)}
                    style={{
                      fontSize: 11,
                      color: radius === km ? ORANGE : '#444',
                      cursor: 'pointer',
                      fontWeight: radius === km ? 700 : 400
                    }}
                  >
                    {km}km
                  </div>
                ))}
              </div>
              <div style={{
                marginTop: 10, fontSize: 11,
                color: '#444', textAlign: 'center'
              }}>
                Showing people within {radius}km of you
              </div>
            </div>
          </div>
        )}

        {/* POPULAR AREAS */}
        <div style={sectionLabel}>Or pick a popular area</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 32 }}>
          {bangaloreAreas.map(area => (
            <button
              key={area}
              onClick={() => setSelectedArea(
                selectedArea === area ? '' : area
              )}
              style={chipStyle(selectedArea === area)}
            >
              📍 {area}
            </button>
          ))}
        </div>

        {/* RESTAURANT */}
        <div style={sectionLabel}>Restaurant in mind? (optional)</div>
        <input
          type="text"
          placeholder="e.g. Meghana Foods, Koramangala"
          value={restaurant}
          onChange={e => setRestaurant(e.target.value)}
          style={{
            width: '100%',
            background: CARD,
            border: `1.5px solid ${BORDER}`,
            borderRadius: 12,
            color: '#fff',
            padding: '12px 16px',
            fontSize: 14,
            outline: 'none',
            fontFamily: "'DM Sans', sans-serif",
            marginBottom: 28,
            boxSizing: 'border-box'
          }}
        />

        {error && (
          <div style={{ color: '#ff4444', fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* POST BUTTON */}
        <button
          onClick={postCraving}
          disabled={!canPost || loading}
          style={{
            width: '100%',
            background: canPost ? ORANGE : CARD,
            color: canPost ? '#fff' : '#444',
            border: 'none',
            borderRadius: 14,
            padding: '15px',
            fontSize: 15,
            fontWeight: 600,
            cursor: canPost ? 'pointer' : 'not-allowed',
            fontFamily: "'DM Sans', sans-serif",
            transition: 'all 0.2s'
          }}
        >
          {loading ? 'Posting...' : 'Find my crew 🔥'}
        </button>

      </div>
    </div>
  )
}